import { useState, useCallback } from 'react';
import { Filter, FilterUIConfig } from '../../types';

export interface UseFiltersParams {
  initialFilters?: Filter[];
  onFiltersChange?: (filters: Filter[]) => void;
}

export interface UseFiltersReturn {
  filters: Filter[];
  addFilter: (filter: Filter) => void;
  updateFilter: (index: number, filter: Filter) => void;
  removeFilter: (index: number) => void;
  clearFilters: () => void;
  hasActiveFilters: boolean;
}

/**
 * Hook for managing table data filters
 *
 * This hook provides comprehensive filter management including:
 * - Adding, updating, and removing individual filters
 * - Clearing all filters at once
 * - Tracking active filter state
 * - Change callbacks for external synchronization
 *
 * @param params - Configuration object containing:
 *   - initialFilters: Array of initial filter configurations
 *   - onFiltersChange: Optional callback when filters change
 *
 * @returns Object containing:
 *   - filters: Current array of active filters
 *   - addFilter: Function to add a new filter
 *   - updateFilter: Function to update an existing filter by index
 *   - removeFilter: Function to remove a filter by index
 *   - clearFilters: Function to remove all filters
 *   - hasActiveFilters: Boolean indicating if any filters are active
 *
 * @example
 * ```tsx
 * const {
 *   filters,
 *   addFilter,
 *   updateFilter,
 *   removeFilter,
 *   clearFilters,
 *   hasActiveFilters
 * } = useFilters({
 *   initialFilters: [
 *     { field: 'status', operator: 'equals', value: 'active' }
 *   ],
 *   onFiltersChange: (filters) => console.log('Filters changed:', filters)
 * });
 *
 * // Add a new filter
 * addFilter({ field: 'name', operator: 'contains', value: 'john' });
 *
 * // Update existing filter
 * updateFilter(0, { field: 'status', operator: 'equals', value: 'inactive' });
 * ```
 */
export function useFilters({
  initialFilters = [],
  onFiltersChange,
}: UseFiltersParams = {}): UseFiltersReturn {
  const [filters, setFilters] = useState<Filter[]>(initialFilters);

  const addFilter = useCallback(
    (filter: Filter) => {
      const newFilters = [...filters, filter];
      setFilters(newFilters);
      onFiltersChange?.(newFilters);
    },
    [filters, onFiltersChange],
  );

  const updateFilter = useCallback(
    (index: number, filter: Filter) => {
      const newFilters = [...filters];
      newFilters[index] = filter;
      setFilters(newFilters);
      onFiltersChange?.(newFilters);
    },
    [filters, onFiltersChange],
  );

  const removeFilter = useCallback(
    (index: number) => {
      const newFilters = filters.filter((_, i) => i !== index);
      setFilters(newFilters);
      onFiltersChange?.(newFilters);
    },
    [filters, onFiltersChange],
  );

  const clearFilters = useCallback(() => {
    setFilters([]);
    onFiltersChange?.([]);
  }, [onFiltersChange]);

  return {
    filters,
    addFilter,
    updateFilter,
    removeFilter,
    clearFilters,
    hasActiveFilters: filters.length > 0,
  };
}

/**
 * Utility functions for working with filters
 */
export const filterUtils = {
  /**
   * Get default operators for a field type
   */
  getDefaultOperators: (type: FilterUIConfig['type']) => {
    switch (type) {
      case 'text':
        return ['contains', 'equals', 'startsWith', 'endsWith'] as const;
      case 'number':
        return [
          'equals',
          'greaterThan',
          'lessThan',
          'greaterThanOrEqual',
          'lessThanOrEqual',
        ] as const;
      case 'date':
        return [
          'equals',
          'greaterThan',
          'lessThan',
          'greaterThanOrEqual',
          'lessThanOrEqual',
        ] as const;
      case 'select':
        return ['equals', 'in', 'notIn'] as const;
      default:
        return ['equals'] as const;
    }
  },

  /**
   * Get human-readable operator label
   */
  getOperatorLabel: (operator: string) => {
    const labels: Record<string, string> = {
      equals: 'Equals',
      contains: 'Contains',
      startsWith: 'Starts with',
      endsWith: 'Ends with',
      greaterThan: 'Greater than',
      lessThan: 'Less than',
      greaterThanOrEqual: 'Greater than or equal',
      lessThanOrEqual: 'Less than or equal',
      in: 'Is one of',
      notIn: 'Is not one of',
    };
    return labels[operator] || operator;
  },

  /**
   * Format filter value for display
   */
  formatFilterValue: (filter: Filter, config?: FilterUIConfig) => {
    if (config?.type === 'select' && config.options) {
      if (Array.isArray(filter.value)) {
        return filter.value
          .map(
            (val) =>
              config.options?.find((opt) => opt.value === val)?.label ||
              String(val),
          )
          .join(', ');
      }
      const option = config.options.find((opt) => opt.value === filter.value);
      return option?.label || String(filter.value);
    }
    return String(filter.value);
  },
};
